import { FormProps as AntdFormProps, Form as AntdForm } from "antd";
import { FormInstance } from "rc-field-form";
import {
  FieldData,
  InternalFormInstance,
  Store,
} from "rc-field-form/lib/interface";
import isDeepEqualReact from "fast-deep-equal/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFirstMountState, usePrevious } from "react-use";
import { HOOK_MARK } from "rc-field-form/lib/FieldContext";

import { FormProps } from "./types";

export const useForm = <T>({
  fields,
  initialValues,
  children: childrenProp,
  onFieldsChange: onFieldsChangeProp,
  onFinish: onFinishProp,
  ...props
}: FormProps<T>): AntdFormProps<T> => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = AntdForm.useForm<T>(props.form);
  const internalFormInstance = form as unknown as InternalFormInstance;
  const { setInitialValues } = useMemo(() => {
    const hooks = internalFormInstance.getInternalHooks(HOOK_MARK);

    if (!hooks) {
      throw new Error("Could not get form hooks.");
    }

    return hooks;
  }, [internalFormInstance]);
  const isFirstRender = useFirstMountState();
  const prevInitialValues = usePrevious(initialValues);
  const prevFields = usePrevious(fields);

  const onFinish = useCallback(
    async (values: T) => {
      const onFinishResult = onFinishProp?.(values);

      if (!(onFinishResult instanceof Promise)) {
        return;
      }

      setIsSubmitting(true);

      try {
        await onFinishResult;
      } finally {
        setIsSubmitting(false);
      }
    },
    [onFinishProp]
  );

  const onFieldsChange = useCallback(
    (changedFields: FieldData[], allFields: FieldData[]) => {
      if (changedFields.length) {
        form.setFields(
          changedFields
            .filter(({ touched }) => touched)
            .map((changedField) => ({
              ...changedField,
              errors: [],
              warnings: [],
            }))
        );
      }

      onFieldsChangeProp?.(changedFields, allFields);
    },
    [form, onFieldsChangeProp]
  );

  useEffect(() => {
    if (!isDeepEqualReact(prevInitialValues, initialValues)) {
      setInitialValues(initialValues || {}, false);
      form.setFieldsValue(initialValues || {});
    }

    if (!isDeepEqualReact(prevFields, fields)) {
      form.setFields(fields || []);
    }
  }, [
    fields,
    form,
    initialValues,
    isFirstRender,
    prevFields,
    prevInitialValues,
    setInitialValues,
  ]);

  const children =
    typeof childrenProp === "function"
      ? (values: Store, formArg: FormInstance<T>): React.ReactNode =>
          childrenProp(values as T, formArg, { isSubmitting })
      : childrenProp;

  return {
    ...props,
    form,
    children,
    onFinish,
    onFieldsChange,
  };
};
